import { useEffect, useMemo, useState } from 'react';
import { parseDate } from '@internationalized/date';
import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  SelectItem,
  TmdxDatepicker,
  TmdxSelect,
  TmdxTextarea,
} from '@ds';
import { IoClose } from 'react-icons/io5';
import { FaInfoCircle } from 'react-icons/fa';
import { toast } from 'react-toastify';
import {
  getPlatformOptions,
  RELEASE_NOTE_PRODUCT_CONFIG,
  RELEASE_NOTE_PRODUCT_LABELS,
  ReleaseNoteProduct,
} from '@features/components/ReleaseNotes';
import { ReleaseNote } from '@/api/apis/release-notes';
import { retrieveUnifiedError } from '@/api/utils';
import { useReleaseNoteFormMutations } from './useReleaseNoteFormMutations';

const MAX_RELEASE_NOTE_TEXT_LENGTH = 2000;

interface ReleaseNoteFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: ReleaseNoteProduct;
  releaseNote?: ReleaseNote;
}

type ReleaseNoteFormData = {
  platform: string;
  version: string;
  releaseDate: string;
  highlights: string;
  bugFixes: string;
};

const initialFormState: ReleaseNoteFormData = {
  platform: '',
  version: '',
  releaseDate: '',
  highlights: '',
  bugFixes: '',
};

const parseTextToArray = (text: string): string[] => {
  return text
    .split(/[\n;]+/)
    .map((item) => item.trim())
    .filter((item) => item !== '');
};

const getOverLimitError = (isOverLimit: boolean) =>
  isOverLimit ? `Must not exceed ${MAX_RELEASE_NOTE_TEXT_LENGTH} characters` : undefined;

export const ReleaseNoteFormModal = ({ isOpen, onClose, product, releaseNote }: ReleaseNoteFormModalProps) => {
  const { saveReleaseNote, isPending } = useReleaseNoteFormMutations(product);

  const isEditMode = !!releaseNote;
  const productLabel = RELEASE_NOTE_PRODUCT_LABELS[product];
  const platformOptions = useMemo(() => getPlatformOptions(RELEASE_NOTE_PRODUCT_CONFIG[product]), [product]);

  const [formData, setFormData] = useState<ReleaseNoteFormData>(initialFormState);

  useEffect(() => {
    if (releaseNote) {
      setFormData({
        platform: releaseNote.platform,
        version: releaseNote.version,
        releaseDate: releaseNote.releaseDate,
        highlights: releaseNote.highlights.join('; '),
        bugFixes: releaseNote.bugFixes.join('; '),
      });
    } else {
      // A product with a single platform has nothing to choose — preselect it.
      const [onlyPlatform] = platformOptions;
      setFormData({
        ...initialFormState,
        platform: platformOptions.length === 1 ? onlyPlatform.key : '',
      });
    }
  }, [releaseNote, isOpen, platformOptions]);

  const handleResetForm = () => {
    setFormData(initialFormState);
  };

  const handleClose = () => {
    handleResetForm();
    onClose();
  };

  const handleSubmit = async () => {
    const payload = {
      version: formData.version,
      releaseDate: formData.releaseDate,
      highlights: parseTextToArray(formData.highlights),
      bugFixes: parseTextToArray(formData.bugFixes),
      ...(platformOptions.length ? { platform: formData.platform } : {}),
    };

    try {
      await saveReleaseNote({ payload, releaseNoteId: releaseNote?.id });
      toast.success(isEditMode ? 'Release note updated successfully' : 'Release note created successfully');
      handleClose();
    } catch (e) {
      const error = retrieveUnifiedError(e as Error);
      toast.error(error.message || 'Failed to save release note');
    }
  };

  const isHighlightsOverLimit = formData.highlights.length > MAX_RELEASE_NOTE_TEXT_LENGTH;
  const isBugFixesOverLimit = formData.bugFixes.length > MAX_RELEASE_NOTE_TEXT_LENGTH;

  const isFormValid =
    (!platformOptions.length || !!formData.platform) &&
    !!formData.version &&
    !!formData.releaseDate &&
    (formData.highlights.trim().length > 0 || formData.bugFixes.trim().length > 0) &&
    !isHighlightsOverLimit &&
    !isBugFixesOverLimit;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      closeButton={<IoClose size={42} className="z-1000" />}
      classNames={{
        closeButton: 'mt-6 mr-4',
        header: 'text-hl! flex flex-col gap-4 text-base-5 font-secondary pt-6 pb-3',
        body: 'block p-0 mx-6 mb-4',
        footer: 'pb-6 px-6',
        base: 'max-w-[600px]',
      }}
    >
      <ModalContent>
        <ModalHeader>{isEditMode ? 'Edit Release Note' : 'Create New Release Note'}</ModalHeader>
        <ModalBody>
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-2 bg-yellow-100 border border-yellow-400 rounded-lg px-3 py-2">
              <FaInfoCircle className="text-yellow-600" size={14} />
              <span className="text-xs text-yellow-800">
                This release note will be created for {productLabel}. Please inform team if you are going to make
                changes.
              </span>
            </div>

            <div className={platformOptions.length ? 'grid grid-cols-2 gap-4' : ''}>
              <TmdxSelect
                label="Platform"
                aria-label="Select platform"
                placeholder="Select platform"
                isDisabled={platformOptions.length === 1}
                selectedKeys={formData.platform ? [formData.platform] : []}
                onSelectionChange={(keys) => {
                  const selectedKey = Array.from(keys)[0] as string;
                  setFormData((prev) => ({ ...prev, platform: selectedKey || '' }));
                }}
              >
                {platformOptions.map((option) => (
                  <SelectItem key={option.key}>{option.label}</SelectItem>
                ))}
              </TmdxSelect>

              <Input
                label="Version"
                placeholder="e.g., 1.12.5"
                value={formData.version}
                onChange={(e) => setFormData((prev) => ({ ...prev, version: e.target.value }))}
              />
            </div>

            <TmdxDatepicker
              label="Release Date"
              aria-label="Release date"
              value={formData.releaseDate ? parseDate(formData.releaseDate) : null}
              onChange={(date) => setFormData((prev) => ({ ...prev, releaseDate: date ? date.toString() : '' }))}
            />

            <TmdxTextarea
              label="Highlights (semicolon separated)"
              placeholder="Highlight A; Highlight B; Highlight C"
              value={formData.highlights}
              onChange={(e) => setFormData((prev) => ({ ...prev, highlights: e.target.value }))}
              maxLength={MAX_RELEASE_NOTE_TEXT_LENGTH}
              errorMessage={getOverLimitError(isHighlightsOverLimit)}
              minRows={3}
            />

            <TmdxTextarea
              label="Bug Fixes (semicolon separated)"
              placeholder="Bug fix A; Bug fix B; Bug fix C"
              value={formData.bugFixes}
              onChange={(e) => setFormData((prev) => ({ ...prev, bugFixes: e.target.value }))}
              maxLength={MAX_RELEASE_NOTE_TEXT_LENGTH}
              errorMessage={getOverLimitError(isBugFixesOverLimit)}
              minRows={3}
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <div className="flex justify-end gap-2 w-full">
            <Button variant="light" size="md" onPress={handleClose}>
              Cancel
            </Button>
            <Button
              variant="solid"
              size="md"
              isDisabled={!isFormValid || isPending}
              isLoading={isPending}
              onPress={handleSubmit}
            >
              {isEditMode ? 'Save' : 'Create'}
            </Button>
          </div>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
